import { DEFAULT_CONFIG, LIMITS, type ActionStreet, type Card, type HandRecord, type HandRecordAction, type HandView, type SeatView, type TableSnapshot } from '@tctp/protocol';
import { cardView } from './cardView';

/** One moment of a finished hand, rendered as if it were live: what the table looked like, and a line saying what just happened. */
export interface ReplayStep { label: string; caption: string; snapshot: TableSnapshot }

type Betting = HandRecordAction & { type: 'fold' | 'check' | 'call' | 'raise' };
interface Moment { label: string; caption: string; upTo: number; street: ActionStreet; discards: boolean; showdown: boolean; board2Cards?: number }   // `board2Cards`: how much of the second board is out, when run twice

const CARDS: Record<ActionStreet, number> = { discard: 0, preflop: 0, flop: 3, turn: 4, river: 5 };
const NAME: Record<ActionStreet, string> = { discard: 'Deal', preflop: 'Preflop', flop: 'Flop', turn: 'Turn', river: 'River' };
const glyph = (c: Card): string => { const v = cardView(c); return `${v.rank}${{ hearts: '♥', diamonds: '♦', spades: '♠', clubs: '♣' }[v.suit] ?? ''}`; };

function verb(a: Betting): string {
  switch (a.type) {
    case 'fold': return 'folds';
    case 'check': return 'checks';
    case 'call': return `calls ${a.amount}`;
    case 'raise': return `raises to ${a.to}`;
  }
}

/** The moments of a hand in order: the deal, the discards, each betting action (with the street's cards arriving before its first action), then the showdown. */
export function replaySteps(r: HandRecord): ReplayStep[] {
  const nick = (seat: number): string => r.players.find(p => p.seat === seat)?.nickname ?? `Seat ${seat + 1}`;
  const betting = r.actions.filter((a): a is Betting => a.type === 'fold' || a.type === 'check' || a.type === 'call' || a.type === 'raise').map(a => ({ ...a, street: a.street ?? 'preflop' }));   // logs from before streets were recorded
  const blinds = r.actions.filter(a => a.type === 'blind');
  const boardText = (n: number): string => r.board.slice(0, n).map(o => glyph(o.card)).join(' ');
  const moments: Moment[] = [];
  moments.push({ label: 'Deal', caption: `Hand #${r.handNumber} · ${blinds.map(b => `${nick(b.seat)} posts ${b.amount}`).join(', ')}`, upTo: 0, street: 'discard', discards: false, showdown: false });
  if (r.discards.length > 0) moments.push({ label: 'Discards', caption: 'Everyone keeps two', upTo: 0, street: 'preflop', discards: true, showdown: false });
  let street: ActionStreet = 'preflop';
  betting.forEach((a, i) => {
    if (a.street !== street) {
      street = a.street;
      moments.push({ label: NAME[street], caption: `${NAME[street]} · ${boardText(CARDS[street])}`, upTo: i, street, discards: true, showdown: false });
    }
    moments.push({ label: NAME[street], caption: `${nick(a.seat)} ${verb(a)}`, upTo: i + 1, street, discards: true, showdown: false });
  });
  for (const s of ['flop', 'turn', 'river'] as const) {   // a run-out after the betting ended (an all-in): the rest of the board, street by street
    if (CARDS[s] > CARDS[street] && r.board.length >= CARDS[s]) {
      street = s;
      moments.push({ label: NAME[s], caption: `${NAME[s]} · ${boardText(CARDS[s])}`, upTo: betting.length, street: s, discards: true, showdown: false });
    }
  }
  const shared = sharedPrefix(r);
  if (r.board2) {   // run twice: the second board's own cards, street by street, after the first board is complete
    for (const s of ['flop', 'turn', 'river'] as const) {
      if (CARDS[s] > shared && r.board2.length >= CARDS[s]) {
        moments.push({ label: 'Run 2', caption: `Run 2 · ${r.board2.slice(0, CARDS[s]).map(o => glyph(o.card)).join(' ')}`, upTo: betting.length, street, discards: true, showdown: false, board2Cards: CARDS[s] });
      }
    }
  }
  const won = new Map<number, number>();
  for (const aw of r.awards) for (const sh of aw.shares) won.set(sh.seat, (won.get(sh.seat) ?? 0) + sh.amount);
  const parts = [...won.entries()].map(([seat, n]) => `${nick(seat)} wins ${n}`);
  if (r.refund) parts.push(`uncalled ${r.refund.amount} back to ${nick(r.refund.seat)}`);
  moments.push({ label: r.showdown.length > 0 ? 'Showdown' : 'Result', caption: parts.join(', '), upTo: betting.length, street, discards: true, showdown: true, board2Cards: r.board2 ? r.board2.length : undefined });
  return moments.map(m => ({ label: m.label, caption: m.caption, snapshot: snapshotAt(r, betting, blinds, m) }));
}

/** How many leading cards the two boards share when the rest was run twice (the record repeats them in `board2`). */
function sharedPrefix(r: HandRecord): number {
  if (!r.board2) return 0;
  let n = 0;
  while (n < r.board.length && n < r.board2.length && r.board[n]!.card === r.board2[n]!.card) n++;
  return n;
}

function snapshotAt(r: HandRecord, betting: Betting[], blinds: HandRecordAction[], m: Moment): TableSnapshot {
  const paid = new Map<number, number>();
  const paidThisStreet = new Map<number, number>();
  const folded = new Set<number>();
  const add = (map: Map<number, number>, seat: number, n: number): void => { map.set(seat, (map.get(seat) ?? 0) + n); };
  for (const b of blinds) { add(paid, b.seat, b.amount ?? 0); if (m.street === 'preflop') add(paidThisStreet, b.seat, b.amount ?? 0); }
  const done = betting.slice(0, m.upTo);
  for (const a of done) {
    if (a.type === 'fold') folded.add(a.seat);
    const n = a.amount ?? 0;
    add(paid, a.seat, n);
    if (a.street === m.street) add(paidThisStreet, a.seat, n);
  }
  const finalStack = new Map(r.finalStacks.map(f => [f.seat, f.stack]));
  const mine = r.mine ?? null;   // the viewer's own dealt cards: only in the copy the server sent them, so their seat plays back as they saw it
  const seats: Array<SeatView | null> = Array.from({ length: LIMITS.maxPlayers }, () => null);
  for (const p of r.players) {
    const stack = m.showdown ? finalStack.get(p.seat) ?? p.startStack - (paid.get(p.seat) ?? 0) : p.startStack - (paid.get(p.seat) ?? 0);
    const sd = r.showdown.find(s => s.seat === p.seat);
    const sd2 = r.showdown2?.find(s => s.seat === p.seat);
    const shown = m.showdown ? sd?.keepers ?? r.shows.find(s => s.seat === p.seat)?.cards ?? [] : [];
    const own = mine !== null && mine.seat === p.seat;
    const discard = m.discards ? r.discards.find(d => d.seat === p.seat)?.opening.card ?? (own ? mine.discard : null) : null;
    seats[p.seat] = {
      seat: p.seat, nickname: p.nickname, stack, connected: true, away: false, busted: false, leaving: false,
      isButton: p.seat === r.buttonSeat, inHand: true, folded: folded.has(p.seat), allIn: !folded.has(p.seat) && stack === 0 && !m.showdown,
      committedThisStreet: m.showdown ? 0 : paidThisStreet.get(p.seat) ?? 0, totalCommitted: paid.get(p.seat) ?? 0,
      discardLocked: m.discards, discard,
      keepers: own && discard !== null ? mine.holeCards.filter(c => c !== discard) : null, shown, holeCards: own ? mine.holeCards : null, handName: m.showdown ? sd?.categoryName ?? null : null, handName2: m.showdown ? sd2?.categoryName ?? null : null, timeBankMs: 0,
    };
  }
  const next = !m.showdown ? betting[m.upTo] : undefined;
  const pot = [...paid.values()].reduce((a, b) => a + b, 0);
  const sb = blinds.find(b => b.amount === r.smallBlind) ?? blinds[0];
  const bb = blinds.find(b => b !== sb) ?? sb;
  const hand: HandView = {
    handId: r.handId,
    phase: m.showdown ? 'complete' : m.street === 'discard' ? 'discard' : m.street,
    buttonSeat: r.buttonSeat, smallBlindSeat: sb?.seat ?? r.buttonSeat, bigBlindSeat: bb?.seat ?? r.buttonSeat,
    board: r.board.slice(0, m.showdown ? r.board.length : CARDS[m.street]).map(o => o.card),
    potTotal: pot, pots: [], currentBet: Math.max(0, ...paidThisStreet.values()),
    actingSeat: next && next.street === m.street ? next.seat : null,
    actionDeadline: null, discardDeadline: null, extraTime: false,
    commitment: r.commitment, openings: [],
    actions: [...blinds, ...done],
    results: m.showdown ? { refund: r.refund, awards: r.awards.map(a => ({ ...a, winnerSeats: a.shares.map(sh => sh.seat) })), finalStacks: r.finalStacks } : null,
    rabbit: null, rabbitHunt: false, runItTwice: null,
    board2: r.board2 && m.board2Cards ? r.board2.slice(0, m.board2Cards).map(o => o.card) : null, runOutFrom: sharedPrefix(r),
  };
  return {
    roomId: r.handId.split(':')[0] ?? 'replay', serverTime: Date.now(),
    you: { seat: mine?.seat ?? null, nickname: mine ? r.players.find(p => p.seat === mine.seat)?.nickname ?? null : null, isHost: false, pendingRequest: null },   // your own hand plays back from your seat at the bottom, as you saw it
    config: { ...DEFAULT_CONFIG, smallBlind: r.smallBlind, bigBlind: r.bigBlind, actionTimerMs: null },
    pendingConfig: null, hostNickname: null, running: true, paused: false, ended: false, endRequested: false,
    seats, hand, legalActions: null, requests: [], chat: [], ledger: [],
  };
}
